/* eslint-disable max-len */
import React, { createContext, useMemo } from 'react';

import { generateCounter, generateCounterContextHook, generateCounterContextProviderHOC } from '../counter-generator';
import { useMax } from '../utils';

const defaultIsLoadingContextValue = { isLoading: false };
const defaultIsTotalLoadingContextValue = { isTotalLoading: false };
const defaultIsLoadingCompletedContextValue = { isLoadingCompleted: false };
const defaultIsTotalLoadingCompletedContextValue = { isTotalLoadingCompleted: false };

const {
  CounterContextProvider,
  useCounterContext: usePromiseCounterContext,
  useCounterScopedContext: usePromiseCounterScopedContext,
  useCounterTotalContext: usePromiseCounterTotalContext,
  useCounterActionContext: usePromiseCounterActionContext,
  withCounterContextProvider,
} = generateCounter({ name: 'Promise' });

const CounterIsLoadingContext = createContext(defaultIsLoadingContextValue);
const CounterIsTotalLoadingContext = createContext(defaultIsTotalLoadingContextValue);
const CounterIsLoadingCompletedContext = createContext(defaultIsLoadingCompletedContextValue);
const CounterIsTotalLoadingCompletedContext = createContext(defaultIsTotalLoadingCompletedContextValue);

const PromiseCounterContextProvider = withCounterContextProvider(({ children }) => {
  const { count } = usePromiseCounterContext();
  const { scopedCount } = usePromiseCounterScopedContext();
  const { totalCount } = usePromiseCounterTotalContext();
  const isLoading = count > 0;
  const isTotalLoading = totalCount > 0;
  const maxCount = useMax(count);
  const maxScopedCount = useMax(scopedCount);
  // This value is used for judging the status that loading started before and has been completed.
  const isLoadingCompleted = !isLoading && maxCount > 0;
  // This value is used for judging the status that total loading started(scoped loading must be there) before,
  // and has been completed. Only when totalCount === 0 and scopedCount ever becomes > 0, this value is true.
  // If you just want to if count is decreased to 0, please use isLoadingCompleted.
  const isTotalLoadingCompleted = !isTotalLoading && maxScopedCount > 0;

  // Memoize context values
  const memoIsLoading = useMemo(() => ({ isLoading }), [isLoading]);
  const memoIsTotalLoading = useMemo(() => ({ isTotalLoading }), [isTotalLoading]);
  const memoIsLoadingCompleted = useMemo(() => ({ isLoadingCompleted }), [isLoadingCompleted]);
  const memoIsTotalLoadingCompleted = useMemo(() => ({ isTotalLoadingCompleted }), [isTotalLoadingCompleted]);

  return (
    <CounterIsLoadingContext.Provider value={memoIsLoading}>
      <CounterIsTotalLoadingContext.Provider value={memoIsTotalLoading}>
        <CounterIsLoadingCompletedContext.Provider value={memoIsLoadingCompleted}>
          <CounterIsTotalLoadingCompletedContext.Provider value={memoIsTotalLoadingCompleted}>
            {children}
          </CounterIsTotalLoadingCompletedContext.Provider>
        </CounterIsLoadingCompletedContext.Provider>
      </CounterIsTotalLoadingContext.Provider>
    </CounterIsLoadingContext.Provider>
  );
});

PromiseCounterContextProvider.displayName = CounterContextProvider.displayName;

const usePromiseCounterIsLoadingContext = generateCounterContextHook(CounterIsLoadingContext);
const usePromiseCounterIsTotalLoadingContext = generateCounterContextHook(CounterIsTotalLoadingContext);
const usePromiseCounterIsLoadingCompletedContext = generateCounterContextHook(CounterIsLoadingCompletedContext);
const usePromiseCounterIsTotalLoadingCompletedContext = generateCounterContextHook(CounterIsTotalLoadingCompletedContext);
const withPromiseCounterContextProvider = generateCounterContextProviderHOC(PromiseCounterContextProvider);

/**
 * Promise Counter Utils
 */
export {
  usePromiseCounterContext,
  usePromiseCounterScopedContext,
  usePromiseCounterTotalContext,
  usePromiseCounterIsLoadingContext,
  usePromiseCounterIsTotalLoadingContext,
  usePromiseCounterIsLoadingCompletedContext,
  usePromiseCounterIsTotalLoadingCompletedContext,
  usePromiseCounterActionContext,
  PromiseCounterContextProvider,
  withPromiseCounterContextProvider,
};
